import classNames from "classnames";
import { CSSProperties, PropsWithChildren, useEffect, useRef, useState } from "react";

type FadeTransitionState = "entering" | "open" | "leaving";

type FadeTransitionProps = {
  isVisible: boolean;
  transitionDuration?: number;
  onTransitionEnd?: () => void;
  className?: string;
} & PropsWithChildren;

export default function FadeTransition({
  isVisible,
  transitionDuration = 1000,
  onTransitionEnd,
  className,
  children,
}: FadeTransitionProps) {
  const [mounted, setMounted] = useState(isVisible);
  const [stage, setStage] = useState<FadeTransitionState>(isVisible ? "entering" : "open");
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isVisible) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setMounted(true);
      setStage("entering");
      requestAnimationFrame(() => setStage("open"));
    } else {
      setStage("leaving");
      timerRef.current = setTimeout(() => {
        setMounted(false);
        onTransitionEnd?.();
      }, transitionDuration);
    }
  }, [isVisible, onTransitionEnd, transitionDuration]);

  if (!mounted) return null;

  return (
    <div
      data-testid="fade-transition"
      className={classNames("transition-opacity linear", stage === "open" ? "opacity-100" : "opacity-0", className)}
      style={{ transitionDuration: `${transitionDuration}ms` } as CSSProperties}
    >
      {children}
    </div>
  );
}
